import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { featureForPath } from '@/lib/features';

const ProtectedRoute = ({ children, section, requireAuth = true }) => {
  const { isAuthenticated, loading, hasAccess, can, isStaff } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E8ECEF] flex items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  // Must be logged in
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const feature = isStaff ? featureForPath(location.pathname) : null;
  if (feature && !can(feature)) {
    return (
      <div className="min-h-screen bg-[#E8ECEF] flex items-center justify-center p-4" data-testid="feature-switched-off">
        <div className="bg-white rounded-2xl p-8 shadow-sm max-w-md text-center">
          <h2 className="text-xl font-bold text-[#27292E] mb-2">This area is switched off for you</h2>
          <p className="text-gray-500 mb-6">An admin can turn it on in Manage Admin Team.</p>
          <button onClick={() => { window.location.href = '/staff'; }} className="bg-[#2F5E96] text-white px-6 py-3 rounded-xl font-medium hover:bg-[#254b7a] transition-colors">Back to dashboard</button>
        </div>
      </div>
    );
  }

  // Must have section access
  if (section && !hasAccess(section)) {
    return (
      <div className="min-h-screen bg-[#E8ECEF] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 shadow-sm max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-[#27292E] mb-2">Access Required</h2>
          <p className="text-gray-500 mb-6">You don&apos;t have access to this section. Request access from the home page.</p>
          <button
            onClick={() => window.location.href = '/'}
            className="bg-[#2F5E96] text-white px-6 py-3 rounded-xl font-medium hover:bg-[#254b7a] transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
